import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, adminAuth } from "@/lib/api";
import { formatApiError } from "@/lib/formatError";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { UserPlus, Link as LinkIcon, FilePlus, Microscope, Loader2, Copy, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const PRO_TIER = ["super_admin", "practitioner_pro"];

export default function PractitionerActions() {
  const navigate = useNavigate();
  const role = adminAuth.getRole();
  const canPro = PRO_TIER.includes(role);

  const [openNewClient, setOpenNewClient] = useState(false);
  const [openIntakeLink, setOpenIntakeLink] = useState(false);
  const [openStartAssessment, setOpenStartAssessment] = useState(false);
  const [openProEval, setOpenProEval] = useState(false);

  return (
    <div className="flex flex-wrap gap-2 mt-6" data-testid="practitioner-action-strip">
      <Button
        size="sm" className="rounded-full bg-purple-900 hover:bg-purple-800 text-white"
        onClick={() => setOpenNewClient(true)}
        data-testid="action-new-client"
      >
        <UserPlus className="h-4 w-4 mr-1.5" /> New Client
      </Button>
      <Button
        size="sm" variant="outline" className="rounded-full border-purple-300 text-purple-900 hover:bg-purple-50"
        onClick={() => setOpenIntakeLink(true)}
        data-testid="action-send-intake-link"
      >
        <LinkIcon className="h-4 w-4 mr-1.5" /> Send Intake Link
      </Button>
      <Button
        size="sm" variant="outline" className="rounded-full border-purple-300 text-purple-900 hover:bg-purple-50"
        onClick={() => setOpenStartAssessment(true)}
        data-testid="action-start-assessment"
      >
        <FilePlus className="h-4 w-4 mr-1.5" /> Start Assessment
      </Button>
      {canPro && (
        <Button
          size="sm" variant="outline" className="rounded-full border-purple-300 text-purple-900 hover:bg-purple-50"
          onClick={() => setOpenProEval(true)}
          data-testid="action-new-pro-evaluation"
        >
          <Microscope className="h-4 w-4 mr-1.5" /> New Pro Evaluation
        </Button>
      )}

      <NewClientDialog open={openNewClient} onOpenChange={setOpenNewClient} onLaunchAssessment={(url) => navigate(url)} />
      <IntakeLinkDialog open={openIntakeLink} onOpenChange={setOpenIntakeLink} />
      <StartAssessmentDialog open={openStartAssessment} onOpenChange={setOpenStartAssessment} onGo={(url) => navigate(url)} />
      {canPro && <ProEvaluationDialog open={openProEval} onOpenChange={setOpenProEval} />}
    </div>
  );
}

function NewClientDialog({ open, onOpenChange, onLaunchAssessment }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", primary_concern: "" });
  const [busy, setBusy] = useState(false);
  const [launch, setLaunch] = useState(true);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { toast.error("Name and email are required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/practitioner/clients", form);
      toast.success("Client saved");
      onOpenChange(false);
      setForm({ name: "", email: "", phone: "", primary_concern: "" });
      if (launch && data.intake_url_for_client) {
        const url = `${data.intake_url_for_client}&prefill_concern=${encodeURIComponent(form.primary_concern || "")}`;
        onLaunchAssessment(url);
      }
    } catch (err) {
      toast.error(formatApiError(err, "Could not create client"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="new-client-dialog">
        <DialogHeader>
          <DialogTitle>New client</DialogTitle>
          <DialogDescription>Create a lightweight client record. Launch their assessment immediately, or come back to it later.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Full name</Label>
            <Input data-testid="new-client-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 rounded-xl border-slate-200 bg-slate-50/60 h-11" required />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Email</Label>
            <Input data-testid="new-client-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 rounded-xl border-slate-200 bg-slate-50/60 h-11" required />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Phone</Label>
            <Input data-testid="new-client-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5 rounded-xl border-slate-200 bg-slate-50/60 h-11" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Primary concern</Label>
            <Textarea data-testid="new-client-concern" value={form.primary_concern} onChange={(e) => setForm({ ...form, primary_concern: e.target.value })} className="mt-1.5 rounded-xl border-slate-200 bg-slate-50/60 min-h-[80px]" placeholder="What brought them in today?" />
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-600">
            <input type="checkbox" checked={launch} onChange={(e) => setLaunch(e.target.checked)} data-testid="new-client-launch-toggle" />
            Launch assessment immediately after saving
          </label>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={busy} className="rounded-full bg-purple-900 hover:bg-purple-800 text-white" data-testid="new-client-save">
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <UserPlus className="h-4 w-4 mr-2" />}
              Save client
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function IntakeLinkDialog({ open, onOpenChange }) {
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      setBusy(true);
      try {
        const { data } = await api.post("/practitioner/intake-links", null, { params: { label: "dashboard" } });
        setUrl(data.intake_url);
      } catch (err) {
        toast.error(formatApiError(err, "Could not generate intake link"));
      } finally {
        setBusy(false);
      }
    })();
  }, [open]);

  const copy = async () => {
    try { await navigator.clipboard.writeText(url); toast.success("Intake link copied"); }
    catch { window.prompt("Copy this intake link:", url); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="intake-link-dialog">
        <DialogHeader>
          <DialogTitle>Send intake link</DialogTitle>
          <DialogDescription>Share this link with your client. Submissions arrive automatically in your dashboard.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Label className="text-xs uppercase tracking-wider text-slate-500">Your unique intake link</Label>
          <div className="flex gap-2">
            <Input value={url} readOnly className="rounded-xl border-slate-200 bg-slate-50/60 font-mono text-xs" data-testid="intake-link-url" />
            <Button onClick={copy} disabled={busy || !url} className="rounded-full bg-purple-900 hover:bg-purple-800 text-white" data-testid="intake-link-copy">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Copy className="h-4 w-4" />}
            </Button>
          </div>
          <p className="text-xs text-slate-500">Any client who opens this link will be assigned to you when they submit their intake.</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ClientPicker({ onSelect, busyState, testIdPrefix }) {
  const [clients, setClients] = useState(null);
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/clients"); setClients(data); }
      catch { setClients([]); }
    })();
  }, []);
  if (clients === null) return <Loader2 className="h-4 w-4 animate-spin text-purple-900" />;
  if (clients.length === 0) return <p className="text-xs text-slate-500 italic">No existing clients yet.</p>;
  return (
    <div className="max-h-60 overflow-auto rounded-xl border border-slate-200">
      {clients.map((c) => (
        <button
          key={c.id} type="button"
          className="w-full text-left p-3 hover:bg-purple-50 flex items-center justify-between border-b border-slate-100 last:border-b-0"
          onClick={() => onSelect(c)}
          data-testid={`${testIdPrefix}-client-${c.id}`}
        >
          <div>
            <div className="font-medium text-sm text-slate-900">{c.name}</div>
            <div className="text-xs text-slate-500">{c.email}</div>
          </div>
          <ArrowRight className="h-4 w-4 text-slate-400" />
        </button>
      ))}
    </div>
  );
}

function StartAssessmentDialog({ open, onOpenChange, onGo }) {
  const [mode, setMode] = useState("new"); // new | existing
  const myPid = adminAuth.getPractitionerId();

  const startNew = () => {
    onOpenChange(false);
    onGo(myPid ? `/intake?practitioner=${myPid}` : "/intake");
  };
  const pickExisting = (c) => {
    onOpenChange(false);
    const url = `/intake?practitioner=${myPid || ""}&prefill_name=${encodeURIComponent(c.name)}&prefill_email=${encodeURIComponent(c.email)}&prefill_phone=${encodeURIComponent(c.phone || "")}`;
    onGo(url);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="start-assessment-dialog">
        <DialogHeader>
          <DialogTitle>Start a new assessment</DialogTitle>
          <DialogDescription>Choose how to begin — for an existing client, or a new walk-in.</DialogDescription>
        </DialogHeader>
        <div className="flex gap-2">
          <Button variant={mode === "new" ? "default" : "outline"} className={mode === "new" ? "rounded-full bg-purple-900 hover:bg-purple-800 text-white" : "rounded-full border-slate-300"} onClick={() => setMode("new")} data-testid="start-mode-new">
            New client
          </Button>
          <Button variant={mode === "existing" ? "default" : "outline"} className={mode === "existing" ? "rounded-full bg-purple-900 hover:bg-purple-800 text-white" : "rounded-full border-slate-300"} onClick={() => setMode("existing")} data-testid="start-mode-existing">
            Existing client
          </Button>
        </div>
        {mode === "new" ? (
          <div className="space-y-3">
            <p className="text-sm text-slate-600">A blank assessment form will open — assigned to you automatically.</p>
            <Button onClick={startNew} className="rounded-full bg-purple-900 hover:bg-purple-800 text-white w-full" data-testid="start-blank-intake">
              <FilePlus className="h-4 w-4 mr-2" /> Open blank intake
            </Button>
          </div>
        ) : (
          <ClientPicker onSelect={pickExisting} testIdPrefix="start-existing" />
        )}
      </DialogContent>
    </Dialog>
  );
}

function ProEvaluationDialog({ open, onOpenChange }) {
  const navigate = useNavigate();
  const pick = (c) => {
    onOpenChange(false);
    navigate(`/admin/pro/${c.id}`);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="pro-eval-dialog">
        <DialogHeader>
          <DialogTitle>New Pro evaluation</DialogTitle>
          <DialogDescription>Pick the client you're evaluating today — a fresh Pro assessment workspace will open.</DialogDescription>
        </DialogHeader>
        <ClientPicker onSelect={pick} testIdPrefix="pro-eval" />
      </DialogContent>
    </Dialog>
  );
}
